/**
 * Rozkład czasu pracy (Etap 41, ADR-0042).
 *
 * Rozkład jest dokumentem z art. 129 § 3 k.p.: sporządza się go na miesiąc
 * i przekazuje pracownikowi co najmniej tydzień przed początkiem okresu. Karta
 * pokazuje trzy rzeczy naraz, bo bez nich rozkład jest tylko tabelką: sumę
 * zaplanowanych godzin wobec wymiaru okresu (art. 130), zastrzeżenia
 * odpoczynkowe (art. 132 i 133) i termin przekazania.
 *
 * Godziny edytuje się per dzień — wolny to po prostu puste pola.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Select,
  Spinner,
  cx,
  useToast,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import {
  api,
  apiDay,
  ApiError,
  apiMonth,
  type ScheduleDayInput,
  type WorkTimeInput,
  type WorkTimeSystem,
} from '../../api/client'
import { FULL_TIME_FTE_BP, WORK_TIME_SYSTEMS, workingTimeFraction } from '@k1p4/shared'
import { useFormat } from '../../format'
import styles from './FamilyMembers.module.css'

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/** Klucz dnia tygodnia do `hrTime.weekdays` — ten sam słownik co kalendarz kadr. */
function weekdayKey(date: string): (typeof WEEKDAY_KEYS)[number] {
  return WEEKDAY_KEYS[new Date(`${date}T00:00:00Z`).getUTCDay()]!
}

function isWeekendDay(date: string): boolean {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay()
  return day === 0 || day === 6
}

export interface WorkScheduleProps {
  employeeId: string
  /** Sporządzanie i przekazanie rozkładu wymaga uprawnienia do czasu pracy. */
  canEdit: boolean
  /**
   * Dzień ustania zatrudnienia ('' — bez daty); `null`, gdy osoba pracuje.
   * Po ustaniu rozkład jest tylko do wglądu (Fala 6, grupa 105).
   */
  employmentEndedOn?: string | null
}

function currentMonth(): string {
  return apiMonth()
}

/** Odmowa po ustaniu zatrudnienia ma własny komunikat z datą ostatniego dnia (przegląd Fali 6). */
function outsideEmployment(cause: unknown): string | null {
  if (!(cause instanceof ApiError) || cause.code !== 'OUTSIDE_EMPLOYMENT') return null
  const lastDay = (cause.details as { lastDay?: string | null } | undefined)?.lastDay ?? null
  /* Klucz komunikatu i data ostatniego dnia — ekran składa z nich zdanie. */
  return lastDay ? `schedule.errorOutsideEmployment@${lastDay}` : 'schedule.errorOutsideEmploymentNoDate'
}

export function WorkSchedule({ employeeId, canEdit: canEditRole, employmentEndedOn = null }: WorkScheduleProps) {
  const fmt = useFormat()
  /* Po ustaniu zatrudnienia nie planuje się pracy — rozkład zostaje do wglądu. */
  const ended = employmentEndedOn !== null
  const canEdit = canEditRole && !ended
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    month: useId(),
    startsAt: useId(),
    endsAt: useId(),
    system: useId(),
    months: useId(),
    start: useId(),
    request: useId(),
    handover: useId(),
  }

  /* Były pracownik: miesiąc ustania, a nie bieżący, którego zatrudnienie już nie obejmuje. */
  const [month, setMonth] = useState(() =>
    employmentEndedOn ? employmentEndedOn.slice(0, 7) : currentMonth(),
  )
  const [pattern, setPattern] = useState({ startsAt: '08:00', endsAt: '16:00' })
  const [draft, setDraft] = useState<readonly ScheduleDayInput[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  /* Ustawienia systemu czasu pracy edytujemy tu, bo one rządzą rozkładem. */
  const [systemDraft, setSystemDraft] = useState<WorkTimeInput | null>(null)
  /*
   * Dzień przekazania jest faktem wpisywanym przez kadry (ADR-0042 pkt 4, fala
   * 7b, grupa 122) — domyślnie dziś w Polsce, nie później niż dziś.
   */
  const [handoverOn, setHandoverOn] = useState(() => apiDay())

  const workTime = useQuery({
    queryKey: ['work-time', employeeId, month],
    queryFn: () => api.workTime(employeeId, month),
  })

  const schedule = useQuery({
    queryKey: ['schedule', employeeId, month],
    queryFn: () => api.schedule(employeeId, month),
  })

  const refresh = (): void => {
    setDraft(null)
    void queryClient.invalidateQueries({ queryKey: ['schedule', employeeId, month] })
  }

  const generate = useMutation({
    mutationFn: () => api.generateSchedule(employeeId, { month, ...pattern }),
    onSuccess: () => {
      toast.show(t('schedule.generated'), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: (cause) => setError(outsideEmployment(cause) ?? 'schedule.errorGeneric'),
  })

  const save = useMutation({
    mutationFn: (days: readonly ScheduleDayInput[]) => api.saveSchedule(employeeId, { month, days }),
    onSuccess: () => {
      toast.show(t('schedule.saved'), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: (cause) => setError(outsideEmployment(cause) ?? 'schedule.errorGeneric'),
  })

  const saveSystem = useMutation({
    mutationFn: (body: WorkTimeInput) => api.saveWorkTime(employeeId, body),
    onSuccess: () => {
      toast.show(t('workTime.saved'), { tone: 'success' })
      setSystemDraft(null)
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['work-time', employeeId] })
      refresh()
    },
    /*
     * Dwa warunki ustawowe mają własne komunikaty: tryb z art. 129 § 2 przy
     * dłuższym okresie i pisemny wniosek pracownika przy art. 143 i 144.
     */
    onError: (cause) =>
      setError(
        cause instanceof ApiError && cause.code === 'ART129_EXTENSION_REQUIRED'
          ? 'workTime.errorArt129'
          : cause instanceof ApiError && cause.code === 'EMPLOYEE_REQUEST_REQUIRED'
            ? 'workTime.errorRequest'
            : 'schedule.errorGeneric',
      ),
  })

  const publish = useMutation({
    mutationFn: () => api.publishSchedule(employeeId, month, handoverOn),
    onSuccess: () => {
      toast.show(t('schedule.published'), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: (cause) =>
      setError(
        outsideEmployment(cause) ??
          (cause instanceof ApiError && cause.code === 'HANDOVER_IN_FUTURE'
            ? 'schedule.errorHandoverFuture'
            : 'schedule.errorGeneric'),
      ),
  })

  const data = schedule.data
  /* Święta miesiąca z odpowiedzi — wiersz dnia je wyróżnia (grupa 125). */
  const holidays = new Set((data?.days ?? []).filter((day) => day.holiday === true).map((day) => day.date))
  /* Miesiąc po miesiącu ustania — rozkładu nie ma i nie planuje się go. */
  const afterEmployment = ended && employmentEndedOn !== null && month > employmentEndedOn.slice(0, 7)
  const days = draft ?? data?.days ?? []

  function setDay(date: string, patch: Partial<ScheduleDayInput>): void {
    setDraft(
      days.map((day) => (day.date === date ? { ...day, ...patch } : day)),
    )
  }

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('schedule.title')}</h3>
        {canEdit && data !== undefined ? (
          <span className={styles.actions}>
            <Input
              id={ids.handover}
              type="date"
              aria-label={t('schedule.publishedOnField')}
              value={handoverOn}
              max={apiDay()}
              onChange={(event) => setHandoverOn(event.target.value)}
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={publish.isPending || days.length === 0 || handoverOn === ''}
              onClick={() => {
                setError(null)
                publish.mutate()
              }}
            >
              {t(data.publishedOn === null ? 'schedule.publish' : 'schedule.changeHandover')}
            </Button>
          </span>
        ) : null}
      </div>
      <p className={styles.note}>{t('schedule.lead')}</p>
      {ended ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('schedule.employmentEnded', {
            date: employmentEndedOn ? formatDate(employmentEndedOn, 'dot') : '—',
          })}
        </InfoBanner>
      ) : null}

      {/*
        System czasu pracy decyduje, jak długą zmianę wolno zaplanować, a okres
        rozliczeniowy — kiedy rozlicza się przeciętną normę tygodniową
        (art. 129 § 1 i § 2 k.p.).
      */}
      {workTime.data !== undefined ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('workTime.summary', {
            system: t(`workTime.systems.${workTime.data.system}`),
            hours: workTime.data.rules.maxDailyHours,
            /* Okres rozliczeniowy to miesiące „rrrr-mm" (fala 9, grupa 151). */
            from: fmt.month(workTime.data.period.from, 'period'),
            to: fmt.month(workTime.data.period.to, 'period'),
            months: workTime.data.period.months,
          })}
          {!workTime.data.stored ? ` ${t('workTime.default')}` : ''}
          {canEdit && systemDraft === null ? (
            <>
              {' '}
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setSystemDraft({
                    system: workTime.data.system,
                    settlementMonths: workTime.data.settlementMonths,
                    settlementStart: workTime.data.settlementStart,
                    art129Extension: workTime.data.art129Extension,
                    employeeRequestOn: workTime.data.employeeRequestOn,
                  })
                }
              >
                {t('workTime.change')}
              </Button>
            </>
          ) : null}
        </InfoBanner>
      ) : null}

      {systemDraft !== null ? (
        <>
          <div className={styles.formGrid}>
            <FormField label={t('workTime.fields.system')} htmlFor={ids.system}>
              <Select
                id={ids.system}
                value={systemDraft.system}
                onChange={(event) =>
                  setSystemDraft({
                    ...systemDraft,
                    system: event.target.value as WorkTimeSystem,
                  })
                }
              >
                {WORK_TIME_SYSTEMS.map((system) => (
                  <option key={system} value={system}>
                    {t(`workTime.systems.${system}`)}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label={t('workTime.fields.months')}
              htmlFor={ids.months}
              hint={t('workTime.fields.monthsHint')}
            >
              <Input
                id={ids.months}
                value={String(systemDraft.settlementMonths)}
                onChange={(event) =>
                  setSystemDraft({
                    ...systemDraft,
                    settlementMonths: Number(event.target.value) || 1,
                  })
                }
              />
            </FormField>
            <FormField label={t('workTime.fields.start')} htmlFor={ids.start}>
              <Input
                id={ids.start}
                type="month"
                value={systemDraft.settlementStart}
                onChange={(event) =>
                  setSystemDraft({ ...systemDraft, settlementStart: event.target.value })
                }
              />
            </FormField>
            <FormField label={t('workTime.fields.request')} htmlFor={ids.request}>
              <Input
                id={ids.request}
                type="date"
                value={systemDraft.employeeRequestOn ?? ''}
                onChange={(event) =>
                  setSystemDraft({
                    ...systemDraft,
                    employeeRequestOn: event.target.value === '' ? null : event.target.value,
                  })
                }
              />
            </FormField>
          </div>
          <Checkbox
            checked={systemDraft.art129Extension === true}
            onChange={(event) =>
              setSystemDraft({ ...systemDraft, art129Extension: event.target.checked })
            }
            label={t('workTime.fields.art129')}
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setSystemDraft(null)}>
              {t('schedule.cancel')}
            </Button>
            <Button
              disabled={saveSystem.isPending}
              onClick={() => {
                setError(null)
                saveSystem.mutate(systemDraft)
              }}
            >
              {saveSystem.isPending ? <Spinner label={t('schedule.saving')} /> : t('workTime.save')}
            </Button>
          </div>
        </>
      ) : null}

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {(() => {
            const [key, date] = error.split('@')
            return t(key!, date ? { date: formatDate(date, 'dot') } : {})
          })()}
        </InfoBanner>
      ) : null}

      <div className={styles.formGrid}>
        <FormField label={t('schedule.month')} htmlFor={ids.month}>
          <Input
            id={ids.month}
            type="month"
            value={month}
            onChange={(event) => {
              setDraft(null)
              setMonth(event.target.value)
            }}
          />
        </FormField>
        {canEdit ? (
          <>
            <FormField label={t('schedule.patternFrom')} htmlFor={ids.startsAt}>
              <Input
                id={ids.startsAt}
                type="time"
                value={pattern.startsAt}
                onChange={(event) => setPattern({ ...pattern, startsAt: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('schedule.patternTo')}
              htmlFor={ids.endsAt}
              hint={t('schedule.patternHint')}
            >
              <Input
                id={ids.endsAt}
                type="time"
                value={pattern.endsAt}
                onChange={(event) => setPattern({ ...pattern, endsAt: event.target.value })}
              />
            </FormField>
            <Button
              size="sm"
              variant="secondary"
              disabled={generate.isPending}
              onClick={() => {
                setError(null)
                generate.mutate()
              }}
            >
              {generate.isPending ? <Spinner label={t('schedule.saving')} /> : t('schedule.generate')}
            </Button>
          </>
        ) : null}
      </div>

      {schedule.isPending ? (
        <Spinner label={t('schedule.loading')} />
      ) : data === undefined ? (
        <EmptyState title={t('common.error')} description={t('common.retry')} />
      ) : afterEmployment ? (
        /* Po ostatnim dniu zatrudnienia nie ma normy ani terminu przekazania (weryfikacja na żywo Fali 6). */
        <EmptyState
          title={t('schedule.afterEmploymentTitle')}
          description={t('schedule.afterEmploymentBody', {
            date: employmentEndedOn ? formatDate(employmentEndedOn, 'dot') : '—',
          })}
        />
      ) : (
        <>
          <InfoBanner
            tone={data.issues.length > 0 || data.handoverOverdue ? 'warning' : 'info'}
            className={styles.banner}
          >
            {/* Przy części etatu „ponad wymiar" liczy się od wymiaru pracownika (fala 7b, #25). */}
            {data.fteBp === FULL_TIME_FTE_BP
              ? t('schedule.summary', {
                  planned: data.plannedHours,
                  norm: data.normHours,
                })
              : t('schedule.summaryPart', {
                  planned: data.plannedHours,
                  norm: data.employeeNormHours,
                  fte: workingTimeFraction(data.fteBp / FULL_TIME_FTE_BP),
                  full: data.normHours,
                })}
            {' '}
            {data.publishedOn === null
              ? t(data.handoverOverdue ? 'schedule.notPublishedOverdue' : 'schedule.notPublished', {
                  date: formatDate(data.handoverDeadline, 'dot'),
                })
              : t('schedule.publishedOn', { date: formatDate(data.publishedOn, 'dot') })}
            {/*
              Zastrzeżenia pokazujemy zawsze, także przed przekazaniem — po
              przekazaniu poprawka wymaga powiadomienia pracownika, a tego
              system nie robi.
            */}
            {data.issues.length > 0 ? (
              <ul>
                {data.issues.map((issue, index) => (
                  <li key={`${issue.kind}-${issue.date ?? String(index)}`}>
                    {t(`schedule.issues.${issue.kind}`, {
                      date: issue.date === null ? '' : formatDate(issue.date, 'dot'),
                      hours: issue.hours ?? 0,
                    })}
                  </li>
                ))}
              </ul>
            ) : null}
          </InfoBanner>

          {days.length === 0 ? (
            <EmptyState title={t('schedule.empty')} description={ended ? undefined : t('schedule.emptyBody')} />
          ) : (
            <>
              <Card pad="flush" className={styles.list}>
                {days.map((day) => (
                  <div
                    key={day.date}
                    className={cx(
                      styles.row,
                      (isWeekendDay(day.date) || holidays.has(day.date)) && styles.rowDayOff,
                    )}
                  >
                    {/* Dzień tygodnia i święto przy dacie, jak w kalendarzu kadr (grupa 125). */}
                    <span className={styles.rowName}>
                      {t(`hrTime.weekdays.${weekdayKey(day.date)}`)} {formatDate(day.date, 'dot')}
                      {holidays.has(day.date) ? (
                        <span className={styles.holidayMark}>{t('hrTime.holiday')}</span>
                      ) : null}
                    </span>
                    {canEdit ? (
                      <>
                        <Input
                          type="time"
                          aria-label={t('schedule.dayFrom', { date: day.date })}
                          value={day.startsAt ?? ''}
                          onChange={(event) =>
                            setDay(day.date, { startsAt: event.target.value || null })
                          }
                        />
                        <Input
                          type="time"
                          aria-label={t('schedule.dayTo', { date: day.date })}
                          value={day.endsAt ?? ''}
                          onChange={(event) =>
                            setDay(day.date, { endsAt: event.target.value || null })
                          }
                        />
                      </>
                    ) : (
                      <span className={styles.rowMeta}>
                        {day.startsAt === null || day.endsAt === null
                          ? t('schedule.dayOff')
                          : `${day.startsAt}–${day.endsAt}`}
                      </span>
                    )}
                  </div>
                ))}
              </Card>
              {canEdit && draft !== null ? (
                <div className={styles.actions}>
                  <Button variant="secondary" onClick={() => setDraft(null)}>
                    {t('schedule.cancel')}
                  </Button>
                  <Button disabled={save.isPending} onClick={() => save.mutate(draft)}>
                    {save.isPending ? <Spinner label={t('schedule.saving')} /> : t('schedule.save')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </section>
  )
}
